/**
 * Git metadata collector
 *
 * Primary source is local git commands. In CI, provider env vars take
 * precedence (they describe the *triggering* ref/commit, which git checkout may
 * have rewritten — e.g. PR merge commits). GitHub Actions keeps its richer
 * event-file PR path; the other providers are resolved from env vars.
 *
 * Provider env var references:
 * - GitHub Actions: https://docs.github.com/en/actions/reference/environment-variables
 * - GitLab CI:      https://docs.gitlab.com/ee/ci/variables/predefined_variables.html
 * - CircleCI:       https://circleci.com/docs/reference/variables/
 * - Azure DevOps:   https://learn.microsoft.com/en-us/azure/devops/pipelines/build/variables
 * - AWS CodeBuild:  https://docs.aws.amazon.com/codebuild/latest/userguide/build-env-ref-env-vars.html
 * - Bitbucket:      https://support.atlassian.com/bitbucket-cloud/docs/variables-and-secrets/
 */

import { readFile } from 'fs/promises';
import { isAbsolute, normalize } from 'path';
import { BaseMetadataCollector } from './base.js';
import { detectCIProvider } from './detect-ci-provider.js';
import type { GitMetadata, PRMetadata } from './types.js';

// Dynamic import for execa (ESM-only package, needs dynamic import in CJS context)
let execaPromise: Promise<typeof import('execa').execa> | null = null;

/**
 * Dynamically import execa (ESM-only package) for CJS compatibility only once per process using memoization.
 *
 * @returns Promise resolving to the execa function
 * @throws Error if execa cannot be imported
 */
async function getExeca(): Promise<typeof import('execa').execa> {
  if (!execaPromise) {
    execaPromise = import('execa')
      .then((m) => m.execa)
      .catch((error) => {
        console.error('Failed to import execa:', error.message);
        // Reset so the next call can retry the import
        execaPromise = null;
        throw new Error('Failed to load execa. Ensure execa is installed: npm install execa');
      });
  }
  return execaPromise;
}

/**
 * Upper bound for the local git-command layer.
 *
 * MetadataAggregator wraps each collector in a 5s budget. Local git commands are
 * best-effort *enrichment*: CI providers already expose branch/commit/repo via
 * env vars (no subprocess). On a busy/CPU-starved runner the git subprocess
 * callbacks can be starved past the budget, so we bound them well under it and
 * fall back to the env-derived metadata. Kept comfortably below 5s so the env
 * merge + return still fit.
 */
const GIT_COMMAND_BUDGET_MS = 2500;

/** Splits a `Name <email>` author (GitLab's CI_COMMIT_AUTHOR); a bare name passes through. */
function parseNameEmail(raw: string): { name: string; email?: string } {
  const match = /^(.*?)\s*<([^<>]+)>\s*$/.exec(raw.trim());
  return match ? { name: match[1].trim(), email: match[2].trim() } : { name: raw.trim() };
}

/**
 * Minimal GitHub event data interface for type safety
 */
interface GitHubEventData {
  ref?: unknown;
  head_commit?: {
    id?: unknown;
    message?: unknown;
    timestamp?: unknown;
    author?: { name?: unknown; email?: unknown; username?: unknown };
  };
  repository?: { html_url?: unknown };
  pull_request?: {
    title?: unknown;
    number?: unknown;
    state?: unknown;
    head?: { ref?: unknown; sha?: unknown };
    base?: { ref?: unknown };
    user?: { login?: unknown };
    labels?: Array<{ name?: unknown }>;
    merged?: unknown;
    mergeable?: unknown;
    merge_commit_sha?: unknown;
  };
}

/**
 * Resolved GitHub author information for deduplication and canonical naming
 */
interface GitHubAuthorInfo {
  authorId: string;
  authorLogin?: string;
  // True only when the login is the commit's own account, not the GITHUB_ACTOR fallback.
  fromCommit?: boolean;
}

/**
 * Git metadata collected from local git commands (the base layer)
 */
interface GitCommandBase {
  branch?: string;
  hash?: string;
  message?: string;
  author?: string;
  email?: string;
  timestamp?: string;
  repoUrl?: string;
  isDirty?: boolean;
}

/**
 * Git metadata extracted from CI provider environment variables
 */
interface EnvironmentGitInfo {
  branch?: string;
  commitHash?: string;
  commitMessage?: string;
  author?: string;
  email?: string;
  repoUrl?: string;
  prId?: string;
  prTitle?: string;
  prUrl?: string;
  prStatus?: string;
  prBranch?: string;
  prTargetBranch?: string;
}

/**
 * Final resolved commit/branch/repo values after merging git + CI env sources
 */
interface ResolvedGit {
  branch?: string;
  hash?: string;
  message?: string;
  author?: string;
  email?: string;
  verifiedLogin?: string;
  timestamp?: string;
  repoUrl?: string;
  pr?: PRMetadata;
}

export interface GitCollectorOptions {
  /**
   * Timeout for git commands in milliseconds
   * @default 3000
   */
  timeout?: number;

  /**
   * Working directory for git commands
   * @default process.cwd()
   */
  cwd?: string;
}

/**
 * Collects git repository metadata
 */
export class GitMetadataCollector extends BaseMetadataCollector<GitMetadata> {
  private options: Required<GitCollectorOptions>;

  constructor(options: GitCollectorOptions = {}) {
    super('git');
    this.options = {
      timeout: options.timeout || 3000,
      cwd: options.cwd || process.cwd(),
    };
  }

  /**
   * Collect git metadata
   */
  protected async collectMetadata(): Promise<GitMetadata> {
    const provider = detectCIProvider();

    // Git commands are best-effort enrichment, bounded so a slow or CPU-starved
    // runner (e.g. a busy container) can't blow the metadata budget. CI
    // providers expose branch/commit/repo via env vars (no subprocess), so that
    // data survives even when the git commands don't finish in time.
    const base = await this.collectGitCommandBase();

    // GitHub Actions keeps the richer event-file PR path; other providers
    // (and local runs) resolve from CI env vars layered over git commands.
    const resolved =
      provider === 'github-actions' ? await this.applyGitHubContext(base) : this.applyCIEnvContext(base, provider);

    // Resolve GitHub author info: numeric ID for deduplication, login for canonical name
    const githubAuthor = await this.resolveGitHubAuthor(resolved.hash);

    // Nothing usable (not a git repo and not in a recognized CI) → empty.
    if (!this.hasGitData(resolved, base)) {
      return this.getEmptyMetadata();
    }

    return {
      branch: resolved.branch,
      commit: {
        hash: resolved.hash,
        message: resolved.message,
        author: githubAuthor.authorLogin || resolved.author,
        authorId: githubAuthor.authorId,
        email: resolved.email,
        verifiedLogin: (githubAuthor.fromCommit ? githubAuthor.authorLogin : undefined) ?? resolved.verifiedLogin,
        timestamp: resolved.timestamp,
        isDirty: base.isDirty,
      },
      repository: {
        name: this.extractRepoName(resolved.repoUrl),
        url: resolved.repoUrl,
      },
      ...(resolved.pr ? { pr: resolved.pr } : {}),
    };
  }

  /**
   * Whether any meaningful git/CI metadata was resolved. Used to preserve the
   * "empty when not a git repo and not in CI" contract.
   */
  private hasGitData(resolved: ResolvedGit, base: GitCommandBase): boolean {
    return Boolean(
      resolved.branch ||
      resolved.hash ||
      resolved.message ||
      resolved.author ||
      resolved.email ||
      resolved.repoUrl ||
      resolved.pr ||
      base.isDirty !== undefined
    );
  }

  /**
   * Get empty metadata
   */
  protected getEmptyMetadata(): GitMetadata {
    return {};
  }

  /**
   * Collect base git metadata. The GIT_COMMAND_BUDGET_MS bound is on how long we
   * *wait* (withTimeout is a Promise.race, not cancellation) — on timeout the
   * git subprocesses keep running detached and are reaped by execa's own
   * per-command `timeout: this.options.timeout`. We just stop awaiting and return
   * an empty base so a slow or CPU-starved runner can't discard CI env-derived
   * metadata (which needs no git subprocess).
   */
  private async collectGitCommandBase(): Promise<GitCommandBase> {
    try {
      return await this.withTimeout(this.runGitCommands(), GIT_COMMAND_BUDGET_MS, 'git command metadata');
    } catch {
      // Slow/starved/unavailable git → CI env metadata still covers providers.
      return {};
    }
  }

  /**
   * Run the local git commands: verify the repo, then collect
   * branch/commit/url/dirty in parallel. Promise.allSettled isolates failures so
   * a single broken command never loses the rest of the data.
   */
  private async runGitCommands(): Promise<GitCommandBase> {
    // Not a git repository → nothing to collect from commands
    if (!(await this.isGitRepository())) {
      return {};
    }

    const results = await Promise.allSettled([
      this.getBranch(),
      this.getCommitHash(),
      this.getCommitInfo(),
      this.getRepoUrl(),
      this.isDirtyWorkingTree(),
    ]);

    const value = <T>(index: number): T | undefined =>
      results[index]?.status === 'fulfilled' ? (results[index] as PromiseFulfilledResult<T>).value : undefined;

    const commitInfo = value<{ message?: string; author?: string; email?: string; timestamp?: string }>(2);

    return {
      branch: value<string>(0),
      hash: value<string>(1),
      message: commitInfo?.message,
      author: commitInfo?.author,
      email: commitInfo?.email,
      timestamp: commitInfo?.timestamp,
      repoUrl: value<string>(3),
      isDirty: value<boolean>(4),
    };
  }

  /**
   * Apply GitHub Actions PR context over the git command base.
   * Overrides merge-commit data with the real PR head commit and builds rich
   * PR metadata from the event file.
   */
  private async applyGitHubContext(base: GitCommandBase): Promise<ResolvedGit> {
    const resolved: ResolvedGit = { ...base };
    const event = await this.readGitHubEventFile();
    const isPullRequest = process.env.GITHUB_EVENT_NAME === 'pull_request';

    // Branch: the *triggering* ref is authoritative. Under actions/checkout the
    // local ref is a detached HEAD, so git's `base.branch` is the literal "HEAD"
    // and must not win. PR → source branch; push/other → the ref name.
    const branch = this.resolveGitHubBranch(isPullRequest, event);
    if (this.isNonEmptyString(branch)) {
      resolved.branch = branch;
    }

    if (isPullRequest && event?.pull_request) {
      // Rich PR path: real head commit (not the ephemeral merge commit) + PR block.
      // The PR event payload lacks the commit message/author, so enrich via git.
      resolved.pr = this.extractPRMetadata(event);
      const headSha = this.asString(event.pull_request.head?.sha);
      if (headSha) {
        resolved.hash = headSha;
        const realCommit = await this.getCommitInfoFromSha(headSha);
        if (realCommit) {
          resolved.message = realCommit.message ?? resolved.message;
          resolved.author = realCommit.author ?? resolved.author;
          resolved.email = realCommit.email ?? resolved.email;
          resolved.timestamp = realCommit.timestamp ?? resolved.timestamp;
        }
      }
    } else if (event?.head_commit) {
      // Push path: the event payload carries the full commit — no git subprocess,
      // so commit/message/author survive even when the git probe times out.
      const commit = event.head_commit;
      resolved.hash = this.asString(commit.id) ?? resolved.hash;
      resolved.message = this.asString(commit.message) ?? resolved.message;
      resolved.author = this.asString(commit.author?.name) ?? resolved.author;
      resolved.email = this.asString(commit.author?.email) ?? resolved.email;
      resolved.timestamp = this.asString(commit.timestamp) ?? resolved.timestamp;
      resolved.verifiedLogin = this.asString(commit.author?.username);
    }

    // Subprocess-free env fallbacks: cover a missing/unreadable event file and a
    // timed-out git probe. GITHUB_SHA is the pushed commit on non-PR events; on a
    // PR it is the merge commit, so only fill it when nothing better was resolved.
    if (!this.isNonEmptyString(resolved.hash)) {
      resolved.hash = this.asString(process.env.GITHUB_SHA) ?? resolved.hash;
    }
    if (!this.isNonEmptyString(resolved.repoUrl)) {
      resolved.repoUrl = this.resolveGitHubRepoUrl(event) ?? resolved.repoUrl;
    }

    return resolved;
  }

  /**
   * The branch that triggered a GitHub Actions run, from env/event only — never
   * the local git ref, which actions/checkout leaves detached ("HEAD").
   */
  private resolveGitHubBranch(isPullRequest: boolean, event: GitHubEventData | undefined): string | undefined {
    if (isPullRequest) {
      return this.asString(process.env.GITHUB_HEAD_REF) ?? this.asString(event?.pull_request?.head?.ref);
    }
    const refName = this.asString(process.env.GITHUB_REF_NAME);
    if (refName) return refName;
    const eventRef = this.asString(event?.ref);
    return eventRef ? this.extractBranchFromRef(eventRef) : undefined;
  }

  /**
   * Repo URL from GitHub env (GITHUB_SERVER_URL + GITHUB_REPOSITORY) or the event
   * payload, credentials stripped defensively.
   */
  private resolveGitHubRepoUrl(event: GitHubEventData | undefined): string | undefined {
    const serverUrl = this.asString(process.env.GITHUB_SERVER_URL);
    const repository = this.asString(process.env.GITHUB_REPOSITORY);
    if (serverUrl && repository) {
      return `${serverUrl}/${repository}`;
    }
    const htmlUrl = this.asString(event?.repository?.html_url);
    return htmlUrl ? this.stripCredentials(htmlUrl) : undefined;
  }

  private asString(value: unknown): string | undefined {
    return this.isNonEmptyString(value) ? value : undefined;
  }

  /**
   * Apply CI provider env vars over the git command base.
   *
   * For known CI providers env vars win (they describe the triggering ref,
   * which checkout may have rewritten). For local/unknown runs git wins and env
   * only fills gaps.
   */
  private applyCIEnvContext(base: GitCommandBase, provider: string | undefined): ResolvedGit {
    const env = this.getEnvironmentGitInfo(provider);
    const prioritizeEnv = provider !== undefined;
    const pick = (envVal?: string, gitVal?: string): string | undefined =>
      prioritizeEnv ? envVal || gitVal : gitVal || envVal;

    return {
      branch: pick(env.branch, base.branch),
      hash: pick(env.commitHash, base.hash),
      message: pick(env.commitMessage, base.message),
      author: pick(env.author, base.author),
      email: pick(env.email, base.email),
      timestamp: base.timestamp, // env sources don't carry a timestamp
      repoUrl: pick(env.repoUrl, base.repoUrl),
      pr: this.buildPRMetadata(env),
    };
  }

  /**
   * Map CI env PR fields onto the PRMetadata shape. Returns undefined when no
   * PR data is present.
   */
  private buildPRMetadata(env: EnvironmentGitInfo): PRMetadata | undefined {
    const pr: PRMetadata = {};

    if (env.prId) {
      const num = Number(env.prId);
      if (Number.isFinite(num)) pr.number = num;
    }
    if (env.prTitle) pr.title = env.prTitle;
    if (env.prUrl) pr.url = env.prUrl;
    if (env.prStatus) pr.status = env.prStatus;
    if (env.prBranch) pr.branch = env.prBranch;
    if (env.prTargetBranch) pr.targetBranch = env.prTargetBranch;

    return Object.keys(pr).length > 0 ? pr : undefined;
  }

  /**
   * Extract git metadata from CI provider environment variables. The
   * provider-specific block runs first, then generic CI vars fill any gaps.
   */
  private getEnvironmentGitInfo(provider: string | undefined): EnvironmentGitInfo {
    const info: EnvironmentGitInfo = {};

    switch (provider) {
      case 'gitlab-ci':
        this.collectGitLabEnv(info);
        break;
      case 'azure-devops':
        this.collectAzureDevOpsEnv(info);
        break;
      case 'jenkins':
        this.collectJenkinsEnv(info);
        break;
      case 'circleci':
        this.collectCircleCIEnv(info);
        break;
      case 'aws-codebuild':
        this.collectAWSCodeBuildEnv(info);
        break;
      case 'bitbucket-pipelines':
        this.collectBitbucketEnv(info);
        break;
      case 'buildkite':
        this.collectBuildkiteEnv(info);
        break;
      case 'harness':
        this.collectHarnessEnv(info);
        break;
      default:
        break;
    }

    this.collectGenericEnv(info);
    return info;
  }

  /**
   * GitLab CI env extraction
   */
  private collectGitLabEnv(info: EnvironmentGitInfo): void {
    const { env } = process;

    if (env.CI_COMMIT_BRANCH) info.branch = env.CI_COMMIT_BRANCH;
    else if (env.CI_MERGE_REQUEST_SOURCE_BRANCH_NAME) info.branch = env.CI_MERGE_REQUEST_SOURCE_BRANCH_NAME;
    else if (env.CI_COMMIT_REF_NAME) info.branch = env.CI_COMMIT_REF_NAME;

    if (env.CI_COMMIT_SHA) info.commitHash = env.CI_COMMIT_SHA;
    if (env.CI_COMMIT_MESSAGE) info.commitMessage = env.CI_COMMIT_MESSAGE;
    if (env.CI_COMMIT_AUTHOR) {
      const { name, email } = parseNameEmail(env.CI_COMMIT_AUTHOR);
      if (name) info.author = name;
      if (email) info.email = email;
    }
    if (env.CI_COMMIT_AUTHOR_EMAIL) info.email = env.CI_COMMIT_AUTHOR_EMAIL;

    // CI_REPOSITORY_URL embeds a gitlab-ci-token — strip it before storing
    if (env.CI_REPOSITORY_URL) info.repoUrl = this.cleanGitLabUrl(env.CI_REPOSITORY_URL);
    else if (env.CI_PROJECT_URL) info.repoUrl = env.CI_PROJECT_URL;

    if (env.CI_MERGE_REQUEST_IID) {
      info.prId = env.CI_MERGE_REQUEST_IID;
      if (env.CI_MERGE_REQUEST_TITLE) info.prTitle = env.CI_MERGE_REQUEST_TITLE;
      if (env.CI_PROJECT_URL) {
        info.prUrl = `${env.CI_PROJECT_URL}/-/merge_requests/${env.CI_MERGE_REQUEST_IID}`;
      }
      if (env.CI_MERGE_REQUEST_SOURCE_BRANCH_NAME) info.prBranch = env.CI_MERGE_REQUEST_SOURCE_BRANCH_NAME;
      if (env.CI_MERGE_REQUEST_TARGET_BRANCH_NAME) info.prTargetBranch = env.CI_MERGE_REQUEST_TARGET_BRANCH_NAME;
    }
  }

  /**
   * Azure DevOps env extraction
   */
  private collectAzureDevOpsEnv(info: EnvironmentGitInfo): void {
    const { env } = process;

    if (env.BUILD_REASON === 'PullRequest' && env.SYSTEM_PULLREQUEST_SOURCEBRANCH) {
      info.branch = this.extractBranchFromRef(env.SYSTEM_PULLREQUEST_SOURCEBRANCH);
    } else if (env.BUILD_SOURCEBRANCH) {
      info.branch = this.extractBranchFromRef(env.BUILD_SOURCEBRANCH);
    } else if (env.BUILD_SOURCEBRANCHNAME) {
      info.branch = env.BUILD_SOURCEBRANCHNAME;
    }

    if (env.BUILD_SOURCEVERSION) info.commitHash = env.BUILD_SOURCEVERSION;
    if (env.BUILD_SOURCEVERSIONMESSAGE) info.commitMessage = env.BUILD_SOURCEVERSIONMESSAGE;
    // BUILD_REQUESTEDFOR is who started the build (sent as ci.build.actor); the author comes from git.
    if (env.BUILD_REPOSITORY_URI) info.repoUrl = this.cleanAzureDevOpsUrl(env.BUILD_REPOSITORY_URI);

    if (env.SYSTEM_PULLREQUEST_PULLREQUESTID) {
      info.prId = env.SYSTEM_PULLREQUEST_PULLREQUESTID;
      if (env.SYSTEM_PULLREQUEST_PULLREQUESTTITLE) info.prTitle = env.SYSTEM_PULLREQUEST_PULLREQUESTTITLE;
      if (env.SYSTEM_PULLREQUEST_SOURCEREPOSITORYURI) {
        // R6: SYSTEM_PULLREQUEST_SOURCEREPOSITORYURI can carry PAT auth
        // (https://user:PAT@dev.azure.com/...). Strip before templating.
        const cleaned = this.stripCredentials(env.SYSTEM_PULLREQUEST_SOURCEREPOSITORYURI);
        if (cleaned) info.prUrl = `${cleaned}/pullrequest/${env.SYSTEM_PULLREQUEST_PULLREQUESTID}`;
      }
    }
  }

  /**
   * Jenkins env extraction (git-plugin + GitHub PR Builder plugin)
   */
  private collectJenkinsEnv(info: EnvironmentGitInfo): void {
    const { env } = process;

    const gitBranch = env.BRANCH_NAME || env.GIT_BRANCH;
    if (gitBranch) info.branch = gitBranch.replace('origin/', '');
    if (env.GIT_COMMIT) info.commitHash = env.GIT_COMMIT;
    if (env.GIT_AUTHOR_NAME) info.author = env.GIT_AUTHOR_NAME;
    if (env.GIT_AUTHOR_EMAIL) info.email = env.GIT_AUTHOR_EMAIL;
    // R2: Jenkins git-plugin sets GIT_URL to the fetch URL, commonly
    // https://user:pat@host/... — strip creds before we persist.
    if (env.GIT_URL) info.repoUrl = this.stripCredentials(env.GIT_URL);

    if (env.ghprbPullId) {
      info.prId = env.ghprbPullId;
      if (env.ghprbPullTitle) info.prTitle = env.ghprbPullTitle;
      // ghprbPullLink is user-facing but still worth cleaning defensively.
      if (env.ghprbPullLink) info.prUrl = this.stripCredentials(env.ghprbPullLink);
    }
  }

  /**
   * CircleCI env extraction
   */
  private collectCircleCIEnv(info: EnvironmentGitInfo): void {
    const { env } = process;

    if (env.CIRCLE_BRANCH) info.branch = env.CIRCLE_BRANCH;
    if (env.CIRCLE_SHA1) info.commitHash = env.CIRCLE_SHA1;
    // CIRCLE_USERNAME is who started the build, not the commit author; the author comes from git.

    if (env.CIRCLE_REPOSITORY_URL) {
      info.repoUrl = this.cleanCircleCIUrl(env.CIRCLE_REPOSITORY_URL);
    } else if (env.CIRCLE_PROJECT_USERNAME && env.CIRCLE_PROJECT_REPONAME) {
      // CircleCI supports GitHub, GitLab, and Bitbucket. Hard-coding
      // github.com would silently mislabel every non-GitHub project — instead
      // resolve the host from a signal:
      //   1. CIRCLE_PROJECT_TYPE (explicit: 'github' / 'bitbucket' / 'gitlab')
      //   2. host of CIRCLE_PULL_REQUEST if a PR run
      // If neither is available, leave repoUrl unset rather than guess.
      const host = this.resolveCircleCIHost();
      if (host) info.repoUrl = `${host}/${env.CIRCLE_PROJECT_USERNAME}/${env.CIRCLE_PROJECT_REPONAME}`;
    }

    if (env.CIRCLE_PULL_REQUEST || env.CIRCLE_PR_NUMBER) {
      if (env.CIRCLE_PR_NUMBER) {
        info.prId = env.CIRCLE_PR_NUMBER;
      } else if (env.CIRCLE_PULL_REQUEST) {
        const prMatch = env.CIRCLE_PULL_REQUEST.match(/\/pull\/(\d+)$/);
        if (prMatch?.[1]) info.prId = prMatch[1];
      }

      // R2: CIRCLE_PULL_REQUEST is normally clean (public PR page) but strip
      // defensively — some proxied setups append tokens.
      if (env.CIRCLE_PULL_REQUEST) info.prUrl = this.stripCredentials(env.CIRCLE_PULL_REQUEST);
      if (!env.CIRCLE_TAG && env.CIRCLE_PULL_REQUEST) info.prStatus = 'open';
    }
  }

  /**
   * AWS CodeBuild env extraction
   */
  private collectAWSCodeBuildEnv(info: EnvironmentGitInfo): void {
    const { env } = process;

    if (env.CODEBUILD_WEBHOOK_HEAD_REF) {
      info.branch = env.CODEBUILD_WEBHOOK_HEAD_REF.replace('refs/heads/', '');
    } else if (env.CODEBUILD_SOURCE_VERSION) {
      const sourceVersion = env.CODEBUILD_SOURCE_VERSION;
      // Skip PR refs (pr/123) and raw commit hashes — only branch names are useful here
      if (!sourceVersion.startsWith('pr/') && !/^[0-9a-f]{40}$/.test(sourceVersion)) {
        info.branch = sourceVersion.replace('refs/heads/', '');
      }
    }

    if (env.CODEBUILD_RESOLVED_SOURCE_VERSION) info.commitHash = env.CODEBUILD_RESOLVED_SOURCE_VERSION;
    if (env.CODEBUILD_SOURCE_REPO_URL) info.repoUrl = this.convertCodeCommitToConsoleUrl(env.CODEBUILD_SOURCE_REPO_URL);

    if (env.CODEBUILD_WEBHOOK_TRIGGER) {
      const prMatch = env.CODEBUILD_WEBHOOK_TRIGGER.match(/^pr\/(\d+)$/);
      if (prMatch?.[1]) {
        info.prId = prMatch[1];
        info.prStatus = 'open';
        if (env.CODEBUILD_SOURCE_REPO_URL) {
          info.prUrl = this.buildProviderPRUrl(env.CODEBUILD_SOURCE_REPO_URL, prMatch[1]);
        }
      }
    }
  }

  /**
   * Build a PR URL from a repo remote URL, parsing the hostname so
   * `github.com` doesn't false-positive against `evil-github.com.attacker.com`
   * and self-hosted / Enterprise hosts (`github.mycompany.com`,
   * `gitlab.internal`, `bitbucket-server.corp`) are still recognized.
   * Accepts HTTPS and SSH remotes (Buildkite's BUILDKITE_REPO is often SSH).
   * Returns undefined when the host is not recognizable — safer than a wrong
   * link.
   */
  private buildProviderPRUrl(repoUrl: string, prId: string): string | undefined {
    // normalizeRemoteToHttps preserves userinfo by contract; strip before pr.url becomes user-visible.
    const normalized = this.normalizeRemoteToHttps(repoUrl);
    const cleaned = this.stripCredentials(normalized) ?? normalized;
    let host: string;
    try {
      host = new URL(cleaned).hostname.toLowerCase();
    } catch {
      return undefined;
    }
    // R4: host === 'X' or host.endsWith('.X') — the leading dot in the suffix
    // check is critical, otherwise `github.evil.com` matches. `startsWith('X.')`
    // was the vulnerable variant: `github.` matches `github.evil.com` and would
    // synthesise a phishing PR link into the dashboard.
    // GitHub Enterprise Server customers can whitelist their host via
    // TESTDINO_GHE_HOSTS (CSV) — off by default because most GHE customers
    // won't run PRs through CodeBuild. See ISSUES.md R4.
    if (host === 'github.com' || host.endsWith('.github.com') || this.isAllowlistedHost(host, 'TESTDINO_GHE_HOSTS')) {
      return `${cleaned}/pull/${prId}`;
    }
    if (host === 'bitbucket.org' || host.endsWith('.bitbucket.org')) {
      return `${cleaned}/pull-requests/${prId}`;
    }
    if (host === 'gitlab.com' || host.endsWith('.gitlab.com')) {
      return `${cleaned}/-/merge_requests/${prId}`;
    }
    return undefined;
  }

  /**
   * Normalize a git remote to an `https://host/path` form (no `.git` suffix) so
   * hostname parsing works uniformly. Handles SSH shorthand
   * (`git@host:org/repo.git`) and `ssh://` / `git://` remotes; HTTPS remotes are
   * returned unchanged apart from the `.git` strip. Credential stripping is left
   * to callers via stripCredentials — this only reshapes the transport.
   */
  private normalizeRemoteToHttps(url: string): string {
    const trimmed = url.trim();

    // git@host:org/repo(.git) → https://host/org/repo
    const sshShorthand = trimmed.match(/^git@([^:]+):(.+?)(?:\.git)?$/i);
    if (sshShorthand) return `https://${sshShorthand[1]}/${sshShorthand[2]}`;

    // ssh://[git@]host/org/repo(.git) or git://host/org/repo(.git) → https://host/org/repo
    const sshProto = trimmed.match(/^(?:ssh|git):\/\/(?:[^@/]+@)?([^/]+)\/(.+?)(?:\.git)?$/i);
    if (sshProto) return `https://${sshProto[1]}/${sshProto[2]}`;

    return trimmed.replace(/\.git$/, '');
  }

  /**
   * Optional user-supplied allowlist for self-hosted git provider hosts.
   * Format: `TESTDINO_GHE_HOSTS=host1,host2`. Empty / unset → no extra hosts.
   */
  private isAllowlistedHost(host: string, envKey: string): boolean {
    const raw = process.env[envKey];
    if (!raw) return false;
    return raw
      .split(',')
      .map((h) => h.trim().toLowerCase())
      .filter(Boolean)
      .includes(host);
  }

  /**
   * Bitbucket Pipelines env extraction
   */
  private collectBitbucketEnv(info: EnvironmentGitInfo): void {
    const { env } = process;

    if (env.BITBUCKET_BRANCH) info.branch = env.BITBUCKET_BRANCH;
    if (env.BITBUCKET_COMMIT) info.commitHash = env.BITBUCKET_COMMIT;

    if (env.BITBUCKET_WORKSPACE && env.BITBUCKET_REPO_SLUG) {
      info.repoUrl = `https://bitbucket.org/${env.BITBUCKET_WORKSPACE}/${env.BITBUCKET_REPO_SLUG}`;
    } else if (env.BITBUCKET_GIT_HTTP_ORIGIN) {
      // R2: Bitbucket Pipelines documents BITBUCKET_GIT_HTTP_ORIGIN as
      // http://x-token-auth:<token>@bitbucket.org/... — live token in the
      // env var. Strip before we persist.
      info.repoUrl = this.stripCredentials(env.BITBUCKET_GIT_HTTP_ORIGIN);
    }

    if (env.BITBUCKET_PR_ID) {
      info.prId = env.BITBUCKET_PR_ID;
      if (env.BITBUCKET_WORKSPACE && env.BITBUCKET_REPO_SLUG) {
        info.prUrl = `https://bitbucket.org/${env.BITBUCKET_WORKSPACE}/${env.BITBUCKET_REPO_SLUG}/pull-requests/${env.BITBUCKET_PR_ID}`;
      }
      // Bitbucket does not expose PR state via env vars; in-PR runs default to
      // 'open'. Post-merge runs (no BITBUCKET_PR_ID) are reclassified to
      // 'merged' by CIMetadataCollector.reconcileWithGit.
      info.prStatus = 'open';
    }
  }

  /**
   * Buildkite env extraction.
   *
   * On PR builds the agent checks out a detached HEAD, so the local git branch
   * is unreliable — BUILDKITE_BRANCH carries the real triggering ref. BUILDKITE_REPO
   * may be SSH (`git@host:org/repo.git`) or HTTPS; the PR URL is reconstructed
   * from it host-aware so we never synthesise a wrong link.
   */
  private collectBuildkiteEnv(info: EnvironmentGitInfo): void {
    const { env } = process;

    if (env.BUILDKITE_BRANCH) info.branch = env.BUILDKITE_BRANCH;
    if (env.BUILDKITE_COMMIT && env.BUILDKITE_COMMIT !== 'HEAD') info.commitHash = env.BUILDKITE_COMMIT;
    if (env.BUILDKITE_MESSAGE) info.commitMessage = env.BUILDKITE_MESSAGE;
    if (env.BUILDKITE_BUILD_AUTHOR) info.author = env.BUILDKITE_BUILD_AUTHOR;
    if (env.BUILDKITE_BUILD_AUTHOR_EMAIL) info.email = env.BUILDKITE_BUILD_AUTHOR_EMAIL;
    if (env.BUILDKITE_REPO) info.repoUrl = this.stripCredentials(this.normalizeRemoteToHttps(env.BUILDKITE_REPO));

    // BUILDKITE_PULL_REQUEST is the PR number, or the literal 'false' otherwise.
    if (env.BUILDKITE_PULL_REQUEST && env.BUILDKITE_PULL_REQUEST !== 'false') {
      info.prId = env.BUILDKITE_PULL_REQUEST;
      info.prBranch = env.BUILDKITE_BRANCH;
      if (env.BUILDKITE_PULL_REQUEST_BASE_BRANCH) info.prTargetBranch = env.BUILDKITE_PULL_REQUEST_BASE_BRANCH;
      // Buildkite exposes no direct PR URL; reconstruct from the repo remote.
      if (env.BUILDKITE_REPO) info.prUrl = this.buildProviderPRUrl(env.BUILDKITE_REPO, env.BUILDKITE_PULL_REQUEST);
      // No PR state is exposed via env; an in-PR build is 'open'.
      info.prStatus = 'open';
    }
  }

  /**
   * Harness CI env extraction.
   *
   * Harness CI is built on Drone and mirrors the documented DRONE_* vars into
   * the step container; those are the reliable source for git context (the
   * HARNESS_* vars cover pipeline/build identity only). On a PR build DRONE_BRANCH
   * is the target branch, so DRONE_SOURCE_BRANCH / DRONE_COMMIT_BRANCH are
   * preferred for the triggering ref.
   */
  private collectHarnessEnv(info: EnvironmentGitInfo): void {
    const { env } = process;

    if (env.DRONE_SOURCE_BRANCH) info.branch = env.DRONE_SOURCE_BRANCH;
    else if (env.DRONE_COMMIT_BRANCH) info.branch = env.DRONE_COMMIT_BRANCH;
    else if (env.DRONE_BRANCH) info.branch = env.DRONE_BRANCH;

    if (env.DRONE_COMMIT_SHA) info.commitHash = env.DRONE_COMMIT_SHA;
    if (env.DRONE_COMMIT_MESSAGE) info.commitMessage = env.DRONE_COMMIT_MESSAGE;
    if (env.DRONE_COMMIT_AUTHOR) info.author = env.DRONE_COMMIT_AUTHOR;
    if (env.DRONE_COMMIT_AUTHOR_EMAIL) info.email = env.DRONE_COMMIT_AUTHOR_EMAIL;
    if (env.DRONE_REPO_LINK) info.repoUrl = this.stripCredentials(this.normalizeRemoteToHttps(env.DRONE_REPO_LINK));

    if (env.DRONE_PULL_REQUEST) {
      info.prId = env.DRONE_PULL_REQUEST;
      if (env.DRONE_SOURCE_BRANCH) info.prBranch = env.DRONE_SOURCE_BRANCH;
      if (env.DRONE_TARGET_BRANCH) info.prTargetBranch = env.DRONE_TARGET_BRANCH;
      if (env.DRONE_REPO_LINK) info.prUrl = this.buildProviderPRUrl(env.DRONE_REPO_LINK, env.DRONE_PULL_REQUEST);
      info.prStatus = 'open';
    }
  }

  /**
   * Generic CI env vars — only fills fields not already set by a provider
   */
  private collectGenericEnv(info: EnvironmentGitInfo): void {
    const { env } = process;

    if (!info.branch) info.branch = env.CI_BRANCH || env.BRANCH_NAME || env.GIT_BRANCH || undefined;
    if (!info.commitHash) info.commitHash = env.CI_COMMIT || env.COMMIT_SHA || env.GIT_COMMIT || undefined;
    if (!info.author) info.author = env.CI_AUTHOR || env.GIT_AUTHOR || env.COMMIT_AUTHOR || undefined;
    // R2: generic CI env repo URLs can carry credentials too. Any provider we
    // haven't specifically mapped falls through here; strip defensively.
    if (!info.repoUrl) {
      info.repoUrl = this.stripCredentials(env.CI_REPOSITORY_URL || env.REPOSITORY_URL || env.GIT_URL || undefined);
    }
  }

  /**
   * Extract a branch name from a git ref (refs/heads/main → main, refs/tags/v1 → v1)
   */
  private extractBranchFromRef(ref: string): string {
    if (ref.startsWith('refs/heads/')) return ref.slice('refs/heads/'.length);
    if (ref.startsWith('refs/tags/')) return ref.slice('refs/tags/'.length);
    return ref;
  }

  /**
   * Strip the gitlab-ci-token credentials from a GitLab repository URL
   */
  private cleanGitLabUrl(url: string): string {
    try {
      const urlObj = new URL(url);
      urlObj.username = '';
      urlObj.password = '';
      return urlObj.toString();
    } catch {
      return url.replace(/gitlab-ci-token:[^@]+@/, '');
    }
  }

  /**
   * Resolve the CircleCI project's host origin (https://<host>). Returns
   * undefined when no reliable signal is available so callers can skip
   * constructing a mislabeled URL rather than defaulting to github.com.
   */
  private resolveCircleCIHost(): string | undefined {
    const { env } = process;
    const hostByType: Record<string, string> = {
      github: 'https://github.com',
      gitlab: 'https://gitlab.com',
      bitbucket: 'https://bitbucket.org',
    };
    if (env.CIRCLE_PROJECT_TYPE) {
      const host = hostByType[env.CIRCLE_PROJECT_TYPE.toLowerCase()];
      if (host) return host;
    }
    if (env.CIRCLE_PULL_REQUEST) {
      try {
        return new URL(env.CIRCLE_PULL_REQUEST).origin;
      } catch {
        /* fall through */
      }
    }
    return undefined;
  }

  /**
   * Normalize a CircleCI repository URL: SSH → HTTPS, strip creds and .git
   */
  private cleanCircleCIUrl(url: string): string {
    if (!url) return '';

    try {
      if (url.startsWith('git@')) {
        const sshMatch = url.match(/git@([^:]+):([^/]+)\/(.+?)(?:\.git)?$/);
        if (sshMatch && sshMatch.length >= 4) {
          const [, host, user, repo] = sshMatch;
          return `https://${host}/${user}/${repo}`;
        }
      }

      const urlObj = new URL(url);
      urlObj.username = '';
      urlObj.password = '';
      if (urlObj.pathname.endsWith('.git')) {
        urlObj.pathname = urlObj.pathname.slice(0, -4);
      }
      return urlObj.toString();
    } catch {
      try {
        return url
          .replace(/^git@([^:]+):/, 'https://$1/')
          .replace(/\.git$/, '')
          .replace(/\/+$/, '');
      } catch {
        return url;
      }
    }
  }

  /**
   * Convert an AWS CodeCommit clone URL to its AWS Console repository URL
   */
  private convertCodeCommitToConsoleUrl(url: string): string {
    if (!url) return '';

    try {
      const match = url.match(/git-codecommit\.([^.]+)\.amazonaws\.com\/v1\/repos\/(.+?)(?:\.git)?$/);
      if (match?.[1] && match[2]) {
        const region = match[1];
        const repoName = match[2];
        return `https://${region}.console.aws.amazon.com/codesuite/codecommit/repositories/${repoName}`;
      }
    } catch {
      // Fall through to returning the original URL
    }

    return url;
  }

  /**
   * Generic credential-stripping for any URL that flows into `run:begin`.
   * Every user-visible URL emitted to ingestion (repository.url, pr.url) MUST
   * go through this — provider env vars routinely embed short-lived tokens
   * (Jenkins `GIT_URL`, Bitbucket `BITBUCKET_GIT_HTTP_ORIGIN`, Azure DevOps
   * `SYSTEM_PULLREQUEST_SOURCEREPOSITORYURI`, and even the local
   * `git config remote.origin.url` when a dev clones with basic auth).
   * Persisted verbatim, those tokens live forever in ingestion JSONB and
   * render on the dashboard.
   *
   * The provider-specific cleaners (cleanGitLabUrl, cleanCircleCIUrl,
   * cleanAzureDevOpsUrl) still exist for provider-specific normalization
   * (SSH→HTTPS, .git-suffix stripping, ci-token regex fallback); they call
   * this as their credential-stripping primitive.
   */
  private stripCredentials(url: string | undefined): string | undefined {
    if (!url) return url;
    try {
      const parsed = new URL(url);
      parsed.username = '';
      parsed.password = '';
      return parsed.toString();
    } catch {
      // Not a URL we can parse — fall back to a defensive substring strip
      // (protocol://user:pass@ → protocol://). Refuses to run on git@ SSH
      // shorthand (no creds to strip there anyway).
      return url.replace(/^([a-z][a-z0-9+.-]*):\/\/[^/@\s]*@/i, '$1://');
    }
  }

  /**
   * Strip credentials from an Azure DevOps repository URL (keeps encoding intact)
   */
  private cleanAzureDevOpsUrl(url: string): string {
    if (!url) return '';

    try {
      const urlObj = new URL(url);
      urlObj.username = '';
      urlObj.password = '';
      return urlObj.toString();
    } catch {
      try {
        return url.replace(/\/\/[^@]+@/, '//');
      } catch {
        return url;
      }
    }
  }

  /**
   * Read and parse the GitHub Actions event file
   */
  private async readGitHubEventFile(): Promise<GitHubEventData | undefined> {
    const eventPath = process.env.GITHUB_EVENT_PATH;
    if (!eventPath) return undefined;

    if (!isAbsolute(normalize(eventPath))) {
      console.warn('⚠️  TestDino: GITHUB_EVENT_PATH is not an absolute path, skipping');
      return undefined;
    }

    try {
      const content = await this.withTimeout(
        readFile(eventPath, 'utf-8'),
        this.options.timeout,
        'GitHub event file read'
      );
      return this.safeJsonParse<GitHubEventData>(content, {});
    } catch (error) {
      console.warn(
        '⚠️  TestDino: Failed to read GitHub event data:',
        error instanceof Error ? error.message : String(error)
      );
      return undefined;
    }
  }

  /**
   * Extract PR metadata from GitHub event data
   */
  private extractPRMetadata(eventData: GitHubEventData): PRMetadata | undefined {
    const pullRequest = eventData?.pull_request;
    if (!pullRequest) return undefined;

    const prMetadata: PRMetadata = {};

    // Basic PR information
    if (this.isNonEmptyString(pullRequest.title)) {
      prMetadata.title = pullRequest.title;
    }

    if (typeof pullRequest.number === 'number') {
      prMetadata.number = pullRequest.number;
    }

    if (this.isNonEmptyString(pullRequest.state)) {
      prMetadata.status = pullRequest.state;
    }

    // Build PR URL
    const serverUrl = process.env.GITHUB_SERVER_URL;
    const repository = process.env.GITHUB_REPOSITORY;
    if (prMetadata.number && serverUrl && repository) {
      prMetadata.url = `${serverUrl}/${repository}/pull/${prMetadata.number}`;
    }

    // Branch information
    if (pullRequest.head?.ref && this.isNonEmptyString(pullRequest.head.ref)) {
      prMetadata.branch = pullRequest.head.ref;
    }

    if (pullRequest.base?.ref && this.isNonEmptyString(pullRequest.base.ref)) {
      prMetadata.targetBranch = pullRequest.base.ref;
    }

    // Author information
    if (pullRequest.user?.login && this.isNonEmptyString(pullRequest.user.login)) {
      prMetadata.author = pullRequest.user.login;
    }

    // Labels
    if (Array.isArray(pullRequest.labels) && pullRequest.labels.length > 0) {
      const labels = pullRequest.labels
        .map((label) => label?.name)
        .filter((name): name is string => this.isNonEmptyString(name));

      if (labels.length > 0) {
        prMetadata.labels = labels;
      }
    }

    // Merge status
    if (typeof pullRequest.merged === 'boolean') {
      prMetadata.merged = pullRequest.merged;
    }

    if (typeof pullRequest.mergeable === 'boolean') {
      prMetadata.mergeable = pullRequest.mergeable;
    }

    if (this.isNonEmptyString(pullRequest.merge_commit_sha)) {
      prMetadata.mergeCommitSha = pullRequest.merge_commit_sha;
    }

    // Return undefined if no meaningful data was extracted
    const hasData = Object.keys(prMetadata).length > 0;
    return hasData ? prMetadata : undefined;
  }

  /**
   * Get commit details for a specific SHA using git show
   * Used to resolve real commit data in PR context (instead of merge commit)
   */
  private async getCommitInfoFromSha(sha: string): Promise<
    | {
        message?: string;
        author?: string;
        email?: string;
        timestamp?: string;
      }
    | undefined
  > {
    try {
      const result = await this.execGit(['show', '-s', '--format=%s%n%an%n%ae%n%aI', sha]);
      const lines = result.split('\n');
      if (lines.length < 4) return undefined;

      return {
        message: this.isNonEmptyString(lines[0]) ? lines[0] : undefined,
        author: this.isNonEmptyString(lines[1]) ? lines[1] : undefined,
        email: this.isNonEmptyString(lines[2]) ? lines[2] : undefined,
        timestamp: this.isNonEmptyString(lines[3]) ? lines[3] : undefined,
      };
    } catch (error) {
      console.warn(
        '⚠️  TestDino: Failed to get commit info from SHA:',
        error instanceof Error ? error.message : String(error)
      );
      return undefined;
    }
  }

  /**
   * Resolve GitHub author info via the Commits API.
   * Only runs on GitHub Actions where GITHUB_REPOSITORY is available.
   *
   * R3 (2026-07-04): honours GITHUB_API_URL (set by both public GitHub Actions
   * and GitHub Enterprise Server Actions). GITHUB_TOKEN is forwarded ONLY to
   * that same host — a GHES token must never be sent to api.github.com.
   * Users can disable the whole lookup with
   * TESTDINO_DISABLE_GITHUB_AUTHOR_LOOKUP=true.
   */
  private async resolveGitHubAuthor(commitHash: string | undefined): Promise<GitHubAuthorInfo> {
    const empty: GitHubAuthorInfo = { authorId: '' };

    if (this.isGitHubAuthorLookupDisabled()) return empty;
    if (process.env.GITHUB_ACTIONS !== 'true') return empty;
    if (!commitHash) return empty;

    const repository = process.env.GITHUB_REPOSITORY;
    if (!repository) return empty;

    const apiBase = this.resolveGitHubApiBase();
    const url = `${apiBase}/repos/${repository}/commits/${commitHash}`;

    try {
      const response = await this.withTimeout(
        fetch(url, { headers: this.buildGitHubHeaders(apiBase) }),
        this.options.timeout,
        'GitHub Commits API'
      );

      if (!response.ok) {
        return empty;
      }

      const data = (await response.json()) as {
        author?: { id?: number; login?: string } | null;
      };

      if (data?.author?.id) {
        const authorId = String(data.author.id);
        const authorLogin = this.isNonEmptyString(data.author.login) ? data.author.login : undefined;
        return { authorId, authorLogin, fromCommit: true };
      }

      return this.resolveGitHubAuthorFromActor();
    } catch (error) {
      console.warn(
        '⚠️  TestDino: Failed to resolve GitHub author from Commits API:',
        error instanceof Error ? error.message : String(error)
      );
      return this.resolveGitHubAuthorFromActor();
    }
  }

  /**
   * True when the user opted out of remote author lookup entirely.
   * Honours TESTDINO_DISABLE_GITHUB_AUTHOR_LOOKUP = '1' | 'true' (case-insensitive).
   */
  private isGitHubAuthorLookupDisabled(): boolean {
    const raw = process.env.TESTDINO_DISABLE_GITHUB_AUTHOR_LOOKUP;
    if (!raw) return false;
    const v = raw.toLowerCase();
    return v === '1' || v === 'true';
  }

  /**
   * Resolve the GitHub API base URL. GitHub Actions on public GitHub sets
   * GITHUB_API_URL to https://api.github.com; on GitHub Enterprise Server it
   * sets the enterprise host (e.g. https://api.github.acme.com). Falling back
   * to the hard-coded public URL would silently break GHES author resolution
   * AND (with GITHUB_TOKEN forwarded unconditionally) leak the enterprise
   * bearer to a third-party host. Trailing slash trimmed.
   */
  private resolveGitHubApiBase(): string {
    const raw = process.env.GITHUB_API_URL;
    if (this.isNonEmptyString(raw)) {
      return raw.replace(/\/+$/, '');
    }
    return 'https://api.github.com';
  }

  /**
   * Build the GitHub API request headers. Authorization is attached ONLY when
   * the token is being sent to the same host GITHUB_API_URL resolves to — this
   * is the defense-in-depth guard on top of resolveGitHubApiBase(): even if a
   * misconfiguration leaves apiBase pointing at api.github.com on a GHES
   * runner, we won't forward the (unrelated) GHES token to it.
   */
  private buildGitHubHeaders(apiBase: string): Record<string, string> {
    const headers: Record<string, string> = {
      Accept: 'application/vnd.github.v3+json',
      'User-Agent': 'testdino-playwright',
    };
    const token = process.env.GITHUB_TOKEN;
    if (!token) return headers;
    try {
      const apiHost = new URL(apiBase).hostname.toLowerCase();
      const rawApiUrl = process.env.GITHUB_API_URL;
      const envHost = this.isNonEmptyString(rawApiUrl) ? new URL(rawApiUrl).hostname.toLowerCase() : 'api.github.com';
      if (apiHost === envHost) {
        headers.Authorization = `token ${token}`;
      }
    } catch {
      // If either URL is malformed, err on the safe side: no Authorization.
    }
    return headers;
  }

  /**
   * Fallback: resolve GitHub author info from GITHUB_ACTOR via the Users API.
   * Uses the same GITHUB_API_URL + token-scope guard as resolveGitHubAuthor (R3).
   */
  private async resolveGitHubAuthorFromActor(): Promise<GitHubAuthorInfo> {
    if (this.isGitHubAuthorLookupDisabled()) return { authorId: '' };
    const actor = process.env.GITHUB_ACTOR;
    if (!actor) {
      return { authorId: '' };
    }

    const apiBase = this.resolveGitHubApiBase();
    const url = `${apiBase}/users/${actor}`;

    try {
      const response = await this.withTimeout(
        fetch(url, { headers: this.buildGitHubHeaders(apiBase) }),
        this.options.timeout,
        'GitHub Users API'
      );

      if (!response.ok) {
        return { authorId: '' };
      }

      const data = (await response.json()) as {
        id?: number;
        login?: string;
      };

      const authorId = data?.id ? String(data.id) : '';
      const authorLogin = this.isNonEmptyString(data?.login) ? data.login : actor;

      return { authorId, authorLogin };
    } catch (error) {
      console.warn(
        '⚠️  TestDino: Failed to resolve GitHub author from GITHUB_ACTOR:',
        error instanceof Error ? error.message : String(error)
      );
      return { authorId: '' };
    }
  }

  /**
   * Check if current directory is a git repository
   */
  private async isGitRepository(): Promise<boolean> {
    try {
      await this.execGit(['rev-parse', '--git-dir']);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get current branch name
   * Uses: git rev-parse --abbrev-ref HEAD
   */
  private async getBranch(): Promise<string | undefined> {
    try {
      const result = await this.execGit(['rev-parse', '--abbrev-ref', 'HEAD']);
      return this.isNonEmptyString(result) ? result : undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * Get current commit hash (full SHA)
   * Uses: git rev-parse HEAD
   */
  private async getCommitHash(): Promise<string | undefined> {
    try {
      const result = await this.execGit(['rev-parse', 'HEAD']);
      return this.isNonEmptyString(result) ? result : undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * Get commit message, author name, email, and timestamp in a single git process.
   * Uses: git log -1 --pretty=format:%s%n%an%n%ae%n%aI
   */
  private async getCommitInfo(): Promise<
    { message?: string; author?: string; email?: string; timestamp?: string } | undefined
  > {
    try {
      const result = await this.execGit(['log', '-1', '--pretty=format:%s%n%an%n%ae%n%aI']);
      const lines = result.split('\n');
      if (lines.length < 4) return undefined;

      return {
        message: this.isNonEmptyString(lines[0]) ? lines[0] : undefined,
        author: this.isNonEmptyString(lines[1]) ? lines[1] : undefined,
        email: this.isNonEmptyString(lines[2]) ? lines[2] : undefined,
        timestamp: this.isNonEmptyString(lines[3]) ? lines[3] : undefined,
      };
    } catch {
      return undefined;
    }
  }

  /**
   * Get remote origin URL
   * Uses: git config --get remote.origin.url
   */
  private async getRepoUrl(): Promise<string | undefined> {
    try {
      const result = await this.execGit(['config', '--get', 'remote.origin.url']);
      if (!this.isNonEmptyString(result)) return undefined;
      // R1: dev/CI clones with credentials embedded — e.g.
      // https://user:ghp_XXX@github.com/org/repo.git — get baked into
      // git config remote.origin.url. Strip before returning so the
      // token never reaches ingestion / the dashboard.
      return this.stripCredentials(result);
    } catch {
      return undefined;
    }
  }

  /**
   * Check if working tree has uncommitted changes
   * Uses: git status --porcelain
   * Returns true if there are any changes (staged, unstaged, or untracked)
   */
  private async isDirtyWorkingTree(): Promise<boolean | undefined> {
    try {
      const result = await this.execGit(['status', '--porcelain']);
      // If output is non-empty, there are changes
      return result.trim().length > 0;
    } catch {
      return undefined;
    }
  }

  /**
   * Extract repository name from remote URL
   * e.g., "https://github.com/user/repo.git" → "repo"
   */
  private extractRepoName(repoUrl: string | undefined): string | undefined {
    if (!repoUrl) return undefined;
    return repoUrl.split('/').pop()?.replace('.git', '') || undefined;
  }

  /**
   * Execute git command with timeout.
   *
   * `-c safe.directory=<cwd>` is prepended in CI so a workspace mounted with
   * different ownership (a common Docker / GitHub-Actions runner shape)
   * doesn't trigger the dubious-ownership refusal. `-c` is per-invocation and
   * writes NOTHING to `~/.gitconfig` — safe on shared runners.
   */
  private async execGit(args: string[]): Promise<string> {
    const execa = await getExeca();
    const isCI = process.env.CI === 'true' || process.env.GITHUB_ACTIONS === 'true';
    const finalArgs = isCI ? ['-c', `safe.directory=${this.options.cwd}`, ...args] : args;
    const { stdout } = await execa('git', finalArgs, {
      cwd: this.options.cwd,
      timeout: this.options.timeout,
      reject: true,
    });

    return stdout.trim();
  }
}
